import {
  type CreateAdminStateDto,
  type ReplaceAdminStateAttachmentRequirementsDto,
  type UpdateAdminStateDto,
  type V2AdminStateAttachmentRequirementsDto,
  type V2AdminStateDto,
  type V2AdminStateLookupDto,
  type V2AdminStatesPageDto,
  type V2AdminStatesSummaryDto,
  v2AdminStatesControllerAttachmentRequirements,
  v2AdminStatesControllerCreate,
  v2AdminStatesControllerDetail,
  v2AdminStatesControllerList,
  v2AdminStatesControllerLookup,
  v2AdminStatesControllerRemove,
  v2AdminStatesControllerReplaceAttachmentRequirements,
  v2AdminStatesControllerSummary,
  v2AdminStatesControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminStateAttachmentRequirementsSchema,
  V2AdminStateLookupSchema,
  V2AdminStateSchema,
  V2AdminStatesPageSchema,
  V2AdminStatesSummarySchema
} from '@idc-forms/schemas/api-v2/admin-states'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Administración de estados del flujo contra la API v2 (ADR 0019/0020). Consume el
 * SDK generado con el Bearer de la sesión staff v2 (`auth.v2AccessToken`), pagina
 * por cursor y usa el contrato de consulta rico. Identifica el estado por `sta_` y
 * cada requisito documental por `atr_`.
 *
 * Los requisitos documentales del portal son un SUBRECURSO: no se escriben por el
 * PATCH del estado. `replaceAttachmentRequirements` reemplaza la lista entera —lo
 * que no viaja, se borra—, que es exactamente lo que hace el editor.
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae el `querySerializer` con
 * bracket notation. El serializer por defecto del SDK usa `deepObject` de UN solo
 * nivel y explota en runtime ante cualquier filtro anidado del ADR 0020.
 */

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type StatesAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type StatesAdminV2Row = V2AdminStatesPageDto['data'][number]
/** Entrada del selector de estados: id público (`sta_`) + lo mínimo para pintarlo. */
export type StateV2LookupOption = V2AdminStateLookupDto['data'][number]
export type StateV2AttachmentRequirement = V2AdminStateAttachmentRequirementsDto['data'][number]

function toListQuery(query: StatesAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const statesAdminV2Service = {
  list: async (accessToken: string, query: StatesAdminV2Query): Promise<V2AdminStatesPageDto> => {
    const result = await v2AdminStatesControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminStatesPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<StatesAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminStatesSummaryDto> => {
    const result = await v2AdminStatesControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminStatesSummarySchema, result.data)
  },

  lookup: async (accessToken: string): Promise<StateV2LookupOption[]> => {
    const result = await v2AdminStatesControllerLookup({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2AdminStateLookupSchema, result.data).data
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminStateDto> => {
    const result = await v2AdminStatesControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminStateSchema, result.data)
  },

  create: async (accessToken: string, body: CreateAdminStateDto): Promise<V2AdminStateDto> => {
    const result = await v2AdminStatesControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  update: async (
    accessToken: string,
    id: string,
    body: UpdateAdminStateDto
  ): Promise<V2AdminStateDto> => {
    const result = await v2AdminStatesControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminStatesControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  },

  attachmentRequirements: async (
    accessToken: string,
    id: string
  ): Promise<StateV2AttachmentRequirement[]> => {
    const result = await v2AdminStatesControllerAttachmentRequirements({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminStateAttachmentRequirementsSchema, result.data).data
  },

  replaceAttachmentRequirements: async (
    accessToken: string,
    id: string,
    body: ReplaceAdminStateAttachmentRequirementsDto
  ): Promise<StateV2AttachmentRequirement[]> => {
    const result = await v2AdminStatesControllerReplaceAttachmentRequirements({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data.data
  }
}
